import { Data, Schema } from 'effect'
import type { Clock } from '@ascleo/types/date-time-validity'
import type { BookAppointmentResponse } from '@ascleo/frontdesk-contracts/scheduling/book-appointment.response'
import type { BookingStatusRequest, BookingStatusResponse } from '@ascleo/frontdesk-contracts/scheduling/recovery'
import { bookingRecoveryPresentation } from './booking-recovery-presentation'
import { BookingRecoveryStorageError, prepareNewBookingRecovery, readBookingRecovery, type BookingRecoveryStorage, type StoredBookingRecovery } from './booking-recovery-storage'

const pointerPrefix = 'frontdesk-booking-active:'
const generationPrefix = 'frontdesk-booking-generation:'
const Generation = Schema.String.pipe(Schema.check(Schema.isPattern(/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/)))
const Pointer = Schema.Struct({ patientId: Schema.String.pipe(Schema.check(Schema.isMinLength(1))), clientSubmissionId: Schema.String.pipe(Schema.check(Schema.isMinLength(1))) })

/** Capture the bounded recovery generation after identification, before starting a flow.
 * @since 1.1.8
 * @example readBookingRecoveryGeneration(localStorage, patientId);
 */
export function readBookingRecoveryGeneration(storage: BookingRecoveryStorage, patientId: string): string {
  try {
    const value = storage.getItem(generationPrefix + patientId)
    return value === null ? '' : Schema.decodeUnknownSync(Generation)(value)
  } catch { throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' }) }
}
function advanceGeneration(storage: BookingRecoveryStorage, patientId: string) {
  const next = globalThis.crypto.randomUUID()
  try { storage.setItem(generationPrefix + patientId, next) }
  catch { throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' }) }
  if (readBookingRecoveryGeneration(storage, patientId) !== next) throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' })
}

/** Unresolved receipts grant status recovery, never an automatic new submission.
 * @since 1.1.8
 * @example new BookingSubmissionPending({ reason: 'outcome-unknown' });
 */
export class BookingSubmissionPending extends Data.TaggedError('BookingSubmissionPending')<{ readonly reason: 'outcome-unknown' | 'patient-mismatch' }> {}

/** A definitive server outcome permits a separately confirmed fresh intent.
 * This result never creates a replacement in the same invocation.
 * @since 1.1.8
 * @example new BookingSubmissionClosed({ reason: 'rejected' });
 */
export class BookingSubmissionClosed extends Data.TaggedError('BookingSubmissionClosed')<{ readonly reason: 'rejected' | 'expired' }> {}

function pointer(storage: BookingRecoveryStorage, patientId: string) {
  let raw: string | null
  try { raw = storage.getItem(pointerPrefix + patientId) }
  catch { throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' }) }
  if (raw === null) return undefined
  try {
    const stored = Schema.decodeUnknownSync(Pointer)(JSON.parse(raw))
    if (stored.patientId !== patientId) throw new Error('Patient binding mismatch')
    return stored
  } catch { throw new BookingRecoveryStorageError({ reason: 'malformed' }) }
}
function clearPointer(storage: BookingRecoveryStorage, patientId: string, clientSubmissionId: string) {
  const active = pointer(storage, patientId)
  if (active === undefined) return
  if (active.clientSubmissionId !== clientSubmissionId) throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' })
  try {
    storage.removeItem(pointerPrefix + patientId)
    if (storage.getItem(pointerPrefix + patientId) !== null) throw new Error('Recovery pointer remains')
  } catch { throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' }) }
}

/** Detect a retained intent for the currently identified patient, without exposing its receipt.
 * Malformed or inaccessible storage remains a typed failure, never a fresh-intent signal.
 * @since 1.1.8
 * @example const retained = hasRetainedBookingIntent(storage, patientId);
 */
export function hasRetainedBookingIntent(storage: BookingRecoveryStorage, patientId: string): boolean {
  return pointer(storage, patientId) !== undefined
}

function isUncertainDispatch(error: unknown): boolean {
  return typeof error === 'object' && error !== null && '_tag' in error && error._tag === 'BookingRecoveryError' && 'reason' in error && error.reason === 'outcome-unknown'
}
const OUTCOME_STATUS_READS = 3
const OUTCOME_STATUS_INTERVAL_MS = 1500
const defaultWait = (milliseconds: number) => new Promise<void>((resolve) => { setTimeout(resolve, milliseconds) })

function isPreDispatchRefusal(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('_tag' in error)) return false
  if (error._tag === 'ExternalAPIError') return true // The coordinator wraps POST failures as outcome-unknown.
  if (!('reason' in error)) return false
  if (error._tag === 'BookingQuestionnaireFailure') return ['invalid', 'unavailable', 'conflict'].includes(String(error.reason))
  if (error._tag === 'BookingContextFailure') return ['unavailable', 'unknown', 'expired', 'revoked', 'patient-mismatch'].includes(String(error.reason))
  return false
}

/** Persist and read back the active capability before its first create request.
 * A pointer retained across reload/reset routes the next attempt to authenticated
 * status. The callback captures transient content; storage sees bounded IDs only.
 * Existing unknown and in-flight states cannot mint a replacement ID; a prepared
 * retry uses the same receipt through a freshly validated create callback.
 * @since 1.1.8
 * @example const result = await submitOrRecoverBooking({ patientId, storage, clock, create, status });
 */
type BookingSubmissionInput = {
  readonly patientId: string
  readonly currentPatientId: () => string | undefined
  readonly storage: BookingRecoveryStorage
  readonly clock: Clock
  readonly create: (receipt: StoredBookingRecovery) => Promise<BookAppointmentResponse>
  readonly status: (request: BookingStatusRequest) => Promise<BookingStatusResponse>
  readonly withOwnership?: (operation: () => Promise<BookAppointmentResponse>) => Promise<BookAppointmentResponse>
  readonly recoveryGeneration?: string
  /** Delay between bounded status reads after an in-flight/uncertain create; injectable for tests. */
  readonly wait?: (milliseconds: number) => Promise<void>
}

/** Acquire exclusive cross-context ownership before inspecting or publishing an intent.
 * Busy or unsupported ownership fails before dispatch; no waiting attempt creates a replacement.
 * @since 1.1.8
 * @example await submitOrRecoverBooking(input);
 */
export async function submitOrRecoverBooking(input: BookingSubmissionInput): Promise<BookAppointmentResponse> {
  return ownBookingAttempt(() => submitOwnedBooking(input), input.withOwnership)
}

/** Check retained status immediately after identification, before current booking eligibility.
 * This operation cannot mint a receipt or dispatch a prepared/new booking. A
 * prepared or uncertain record remains fenced until assistance or a separate
 * fully validated confirmation; absence permits the ordinary questionnaire flow.
 * @since 1.1.8
 * @example await recoverRetainedBooking({ patientId, currentPatientId, storage, clock, status });
 */
export async function recoverRetainedBooking(input: Omit<BookingSubmissionInput, 'create' | 'withOwnership'> & {
  readonly withOwnership?: (operation: () => Promise<BookAppointmentResponse | undefined>) => Promise<BookAppointmentResponse | undefined>
}): Promise<BookAppointmentResponse | undefined> {
  const { withOwnership, ...retainedInput } = input
  const recoveryGeneration = input.recoveryGeneration ?? readBookingRecoveryGeneration(input.storage, input.patientId)
  const checkIdentification = () => {
    if (input.currentPatientId() !== input.patientId) throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
    if (recoveryGeneration !== readBookingRecoveryGeneration(input.storage, input.patientId)) throw new BookingSubmissionClosed({ reason: 'expired' })
  }
  checkIdentification()
  if (!hasRetainedBookingIntent(input.storage, input.patientId)) return undefined
  return ownBookingAttempt(async () => {
    // Re-read under cross-context ownership. Another tab may have retired the
    // pointer while ownership was acquired; that must never mint a replacement
    // or silently renew identification after an expiry-generation change.
    checkIdentification()
    if (!hasRetainedBookingIntent(input.storage, input.patientId)) return undefined
    return submitOwnedBooking({ ...retainedInput, recoveryGeneration, create: async () => { throw new BookingSubmissionPending({ reason: 'outcome-unknown' }) } })
  }, withOwnership)
}

async function ownBookingAttempt<Result>(operation: () => Promise<Result>, withOwnership?: (operation: () => Promise<Result>) => Promise<Result>): Promise<Result> {
  if (withOwnership !== undefined) return withOwnership(operation)
  if (typeof navigator === 'undefined' || navigator.locks === undefined) throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' })
  return navigator.locks.request('frontdesk-booking-attempt', { ifAvailable: true }, async (lock) => {
    if (lock === null) throw new BookingSubmissionPending({ reason: 'outcome-unknown' })
    return operation()
  })
}

async function submitOwnedBooking(input: BookingSubmissionInput): Promise<BookAppointmentResponse> {
  if (input.patientId.length === 0 || input.currentPatientId() !== input.patientId) throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
  if (input.recoveryGeneration !== undefined && input.recoveryGeneration !== readBookingRecoveryGeneration(input.storage, input.patientId)) throw new BookingSubmissionClosed({ reason: 'expired' })
  // A concurrent attempt owning dispatch, or a lost create response, is a status
  // question rather than a failure: read the recorded outcome a bounded number of
  // times under the same receipt and never dispatch again from here.
  const awaitRecordedOutcome = async (receipt: StoredBookingRecovery): Promise<BookAppointmentResponse> => {
    const wait = input.wait ?? defaultWait
    for (let read = 0; read < OUTCOME_STATUS_READS; read++) {
      if (read > 0) await wait(OUTCOME_STATUS_INTERVAL_MS)
      const status = await input.status({ clientSubmissionId: receipt.clientSubmissionId, recoveryReceipt: receipt.recoveryReceipt })
      const presentation = bookingRecoveryPresentation(status, input.currentPatientId())
      if (input.currentPatientId() !== input.patientId || presentation.kind === 'patient-mismatch' || presentation.kind === 'identification-required') throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
      if (status.status === 'created' || status.status === 'verified') {
        clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
        return { scheduleId: status.scheduleId, startTime: status.binding.startTime, endTime: status.binding.endTime, replayed: true }
      }
      if (status.status === 'rejected' || status.status === 'expired') {
        if (status.status === 'expired') advanceGeneration(input.storage, input.patientId)
        clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
        throw new BookingSubmissionClosed({ reason: status.status })
      }
    }
    throw new BookingSubmissionPending({ reason: 'outcome-unknown' })
  }
  const resolveRefusal = async (error: unknown, receipt: StoredBookingRecovery, fresh: boolean): Promise<BookAppointmentResponse> => {
    if (isUncertainDispatch(error)) return awaitRecordedOutcome(receipt)
    if (!isPreDispatchRefusal(error)) throw error
    const status = await input.status({ clientSubmissionId: receipt.clientSubmissionId, recoveryReceipt: receipt.recoveryReceipt })
    const presentation = bookingRecoveryPresentation(status, input.currentPatientId())
    if (input.currentPatientId() !== input.patientId || presentation.kind === 'patient-mismatch' || presentation.kind === 'identification-required') throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
    if (status.status === 'rejected' || status.status === 'expired' || fresh && status.status === 'unknown') {
      if (status.status === 'expired') advanceGeneration(input.storage, input.patientId)
      clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
      throw new BookingSubmissionClosed({ reason: status.status === 'expired' ? 'expired' : 'rejected' })
    }
    if (status.status === 'created' || status.status === 'verified') {
      clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
      return { scheduleId: status.scheduleId, startTime: status.binding.startTime, endTime: status.binding.endTime, replayed: true }
    }
    throw error
  }
  const active = pointer(input.storage, input.patientId)
  if (active !== undefined) {
    let receipt: StoredBookingRecovery
    try { receipt = readBookingRecovery(input.storage, input.clock, active.clientSubmissionId) }
    catch (error) {
      if (!(error instanceof BookingRecoveryStorageError) || error.reason !== 'expired') throw error
      if (input.currentPatientId() !== input.patientId) throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
      advanceGeneration(input.storage, input.patientId)
      clearPointer(input.storage, input.patientId, active.clientSubmissionId)
      // The capability has already become bounded expiry metadata. Its cleanup
      // is optional after the durable retirement gate succeeds.
      try { input.storage.removeItem('frontdesk-booking-recovery:' + active.clientSubmissionId) } catch { /* No raw receipt remains. */ }
      // Expiry closes this invocation. The view requires reset/identification;
      // no replacement receipt or create is permitted here.
      throw new BookingSubmissionClosed({ reason: 'expired' })
    }
    const outcome = await input.status({ clientSubmissionId: receipt.clientSubmissionId, recoveryReceipt: receipt.recoveryReceipt })
    const presentation = bookingRecoveryPresentation(outcome, input.currentPatientId())
    if (presentation.kind === 'patient-mismatch' || presentation.kind === 'identification-required') throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
    if (outcome.status === 'rejected' || outcome.status === 'expired') {
      if (outcome.status === 'expired') advanceGeneration(input.storage, input.patientId)
      clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
      throw new BookingSubmissionClosed({ reason: outcome.status })
    }
    if (outcome.status === 'prepared') {
      // The server still compares immutable semantics and CAS-acquires prepared
      // only. A changed intent or a concurrently acquired lease cannot bypass it.
      let result: BookAppointmentResponse
      try { result = await input.create(receipt) }
      catch (error) { return resolveRefusal(error, receipt, false) }
      if (input.currentPatientId() !== input.patientId) throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
      clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
      return result
    }
    if (outcome.status !== 'created' && outcome.status !== 'verified') throw new BookingSubmissionPending({ reason: 'outcome-unknown' })
    clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
    return { scheduleId: outcome.scheduleId, startTime: outcome.binding.startTime, endTime: outcome.binding.endTime, replayed: true }
  }
  const receipt = prepareNewBookingRecovery(input.storage, input.clock)
  try { input.storage.setItem(pointerPrefix + input.patientId, JSON.stringify({ patientId: input.patientId, clientSubmissionId: receipt.clientSubmissionId })) }
  catch { throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' }) }
  const persisted = pointer(input.storage, input.patientId)
  if (persisted?.clientSubmissionId !== receipt.clientSubmissionId) throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' })
  let result: BookAppointmentResponse
  try { result = await input.create(receipt) }
  catch (error) {
    return resolveRefusal(error, receipt, true)
  }
  if (input.currentPatientId() !== input.patientId) throw new BookingSubmissionPending({ reason: 'patient-mismatch' })
  clearPointer(input.storage, input.patientId, receipt.clientSubmissionId)
  return result
}
